import {
  getEtcProductMaterialGradeApi,
  getMaterialGradeApi,
  getOptionsApi,
  getProductCategoryApi,
  getProductSellableApi,
  getTypeFirstApi,
  getTypeSecondApi,
  getTypeThirdApi,
  trackingMaterialGradeLogApi,
  getProductTypeApi,
  getProductOneCategoryApi,
  getSaleProductOption,
  getStandardCodeApi,
  getProductWeightApi,
  getProductTypesApi,
  getSupplierDealProductApi,
  getSupplierDealProductOptionApi,
} from '@apis/product';
import { getStockProductTypeApi } from '@apis/stock';
import { Toast } from '@components/_atoms/Toast';
import { SaleProductOptionPayload } from '@recoil/service/SaleProductAlarm';
import { AxiosError } from 'axios';
import {
  getMaterialGradePayload,
  getOptionsPayload,
  getProductCategoryPayload,
  getTypeFirstPayload,
  getTypeSecondPayload,
  getTypeThirdPayload,
} from 'models/Supplement';
import { CommonAxiosErrorType } from 'models/SupplementType';
import {
  ProductEtcGetEtcProductMaterialGradePayload,
  ProductGetMaterialGradeError,
  ProductGetOptionsError,
  ProductGetProductCategoryError,
  ProductLogPostMaterialGradeSearchLogError,
  ProductLogPostMaterialGradeSearchLogPayload,
  ProductTypeGetTypeFirstError,
  ProductTypeGetTypeSecondError,
  ProductTypeGetTypeThirdError,
  SellableProductRequest,
  ProductGetProductTypeData,
  ProductGetOneCategoryError,
  ProductSaleGetSaleProductOptionError,
  ProductGetProductWeightPayload,
} from 'models/swagger/data-contracts';
import { useInfiniteQuery, useMutation, useQuery } from 'react-query';


export const useGetStandardCode = () => {
  const {
    data: getStandardCodeFetchData,
    isLoading: getStandardCodeFetchIsLoading,
    isError: getStandardCodeFetchIsError,
  } = useQuery(
    ['getStandardCode'],
    async () => {
      const result = await getStandardCodeApi();
      return result?.data.data;
    },
    {
      onError: (err: AxiosError<any>) => {
        if (err.response?.data?.message) {
          Toast.error(err.response.data.message.text);
        }
      },
    },
  );

  const isCustomLoading = (!getStandardCodeFetchData || getStandardCodeFetchIsLoading) && !getStandardCodeFetchIsError;

  return {
    getStandardCodeFetchData,
    isCustomLoading,
  };
};


export const useGetOneCategory = (category: number) => {
  const {
    data: getOneCategoryFetchData,
    isLoading: getOneCategoryFetchIsLoading,
    isError: getOneCategoryFetchIsError,
  } = useQuery(
    ['getOneCategory', category],
    async () => {
      const result = await getProductOneCategoryApi({ category });
      return result?.data.data;
    },
    {
      onError: (err: AxiosError<ProductGetOneCategoryError>) => {
        if (err.response?.data?.message) {
          Toast.error(err.response.data.message.text);
        }
      },
    },
  );

  const isCustomLoading = (!getOneCategoryFetchData || getOneCategoryFetchIsLoading) && !getOneCategoryFetchIsError;

  return {
    getOneCategoryFetchData,
    isCustomLoading,
  };
};

/**
 * product_type의 카테고리 가져오기 Fetch
 * @function useGetProductCategoryFetch
 */
export const useGetProductCategoryFetch = (product_type_id: number, standardCode: string, is_etc: number | null = null) => {
  const {
    data: getProductCategoryFetchData,
    isLoading: getProductCategoryFetchIsLoading,
    isError: getProductCategoryFetchIsError,
  } = useQuery(
    ['getProductCategory', product_type_id, standardCode, is_etc],
    async () => {
      const result = await getProductCategoryApi({ product_type_id, standard_code: standardCode, is_etc: is_etc });
      return result?.data.data;
    },
    {
      onError: (err: AxiosError<ProductGetProductCategoryError>) => {
        if (err.response?.data?.message) {
          Toast.error(err.response.data.message.text);
        }
      },
    },
  );

  const isCustomLoading =
    (!getProductCategoryFetchData || getProductCategoryFetchIsLoading) && !getProductCategoryFetchIsError;

  return {
    getProductCategoryFetchData,
    isCustomLoading,
  };
};

/**
 * product_type의 카테고리 가져오기 Fetch (Mutation 버전)
 * @function useGetProductCategoryMutation
 */
export const useGetProductCategoryMutation = () => {
  const { mutateAsync: getProductCategoryMutateAsync, isLoading: getProductCategoryMutateIsLoading } = useMutation(
    ['getProductCategory'],
    async (product_type_id: number) => {
      const result = await getProductCategoryApi({ product_type_id });
      return result?.data.data;
    },
    {
      onError: (err: AxiosError<ProductGetProductCategoryError>) => {
        if (err.response?.data?.message) {
          Toast.error(err.response.data.message.text);
        }
      },
    },
  );

  return {
    getProductCategoryMutateAsync,
    getProductCategoryMutateIsLoading,
  };
};

/**
 * 카테고리로 Material Grade 가져오기 Fetch
 * @function useGetMaterialGradeFetch
 */
export const useGetMaterialGradeFetch = ({ category_id, material_grade, standard_code }: getMaterialGradePayload) => {
  const { data: getMaterialGradeFetchData, fetchNextPage: getMaterialGradeNextFetch } = useInfiniteQuery(
    ['getMaterialGrade', category_id, material_grade, standard_code],
    async ({ pageParam = 1 }) => {
      const result = await getMaterialGradeApi({ page: pageParam, category_id, material_grade, standard_code });
      return result?.data.data;
    },
    {
      getNextPageParam: (lastPage, allPages) => {
        const next = lastPage?.page?.next;
        if (next) {
          return next;
        }
      },
      onError: (err: AxiosError<ProductGetMaterialGradeError>) => {
        if (err.response?.data?.message) {
          Toast.error(err.response.data.message.text);
        }
      },
    },
  );

  return {
    getMaterialGradeFetchData,
    getMaterialGradeNextFetch,
  };
};

/**
 * 타입1 가져오기 Fetch
 * @function useGetTypeFirstFetch
 */
export const useGetTypeFirstFetch = ({ category, material_grade }: getTypeFirstPayload) => {
  const { data: getTypeFirstFetchData, isLoading: getTypeFirstFetchIsLoading } = useQuery(
    ['getType1', category, material_grade],
    async () => {
      const result = await getTypeFirstApi({ category, material_grade });
      return result?.data.data;
    },
    {
      enabled: !!category && !!material_grade,
      onError: (err: AxiosError<ProductTypeGetTypeFirstError>) => {
        if (err.response?.data?.message) {
          Toast.error(err.response.data.message.text);
        }
      },
    },
  );

  return {
    getTypeFirstFetchData,
    getTypeFirstFetchIsLoading,
  };
};

/**
 * 타입2 가져오기 Fetch
 * @function useGetTypeSecondFetch
 */
export const useGetTypeSecondFetch = ({ category, material_grade, type1 }: getTypeSecondPayload) => {
  const { data: getTypeSecondFetchData, isLoading: getTypeSecondFetchIsLoading } = useQuery(
    ['getType2', category, material_grade, type1],
    async () => {
      const result = await getTypeSecondApi({ category, material_grade, type1 });
      return result?.data.data;
    },
    {
      enabled: !!category && !!material_grade && !!type1,
      onError: (err: AxiosError<ProductTypeGetTypeSecondError>) => {
        if (err.response?.data?.message) {
          Toast.error(err.response.data.message.text);
        }
      },
    },
  );

  return {
    getTypeSecondFetchData,
    getTypeSecondFetchIsLoading,
  };
};

/**
 * 타입3 가져오기 Fetch
 * @function useGetTypeThirdFetch
 */
export const useGetTypeThirdFetch = ({ category, material_grade, type1, type2 }: getTypeThirdPayload) => {
  const { data: getTypeThirdFetchData, isLoading: getTypeThirdFetchIsLoading } = useQuery(
    ['getType3', category, material_grade, type1, type2],
    async () => {
      const result = await getTypeThirdApi({ category, material_grade, type1, type2 });
      return result?.data.data;
    },
    {
      enabled: !!category && !!material_grade && !!type1 && !!type2,
      onError: (err: AxiosError<ProductTypeGetTypeThirdError>) => {
        if (err.response?.data?.message) {
          Toast.error(err.response.data.message.text);
        }
      },
    },
  );

  return {
    getTypeThirdFetchData,
    getTypeThirdFetchIsLoading,
  };
};

/**
 * 옵션 가져오기 Fetch
 * @function useGetOptionsFetch
 */
export const useGetOptionsFetch = ({ category, material_grade, type1, type2, type3 }: getOptionsPayload) => {
  const { data: getOptionsFetchData, isLoading: getOptionsFetchIsLoading } = useQuery(
    ['getOptions', category, material_grade, type1, type2, type3],
    async () => {
      const result = await getOptionsApi({ category, material_grade, type1, type2, type3 });
      return result?.data.data;
    },
    {
      enabled: !!category && !!material_grade && !!type1,
      onError: (err: AxiosError<ProductGetOptionsError>) => {
        if (err.response?.data?.message) {
          Toast.error(err.response.data.message.text);
        }
      },
    },
  );

  return {
    getOptionsFetchData,
    getOptionsFetchIsLoading,
  };
};

/**
 *  Material Grade 검색 로그 수집 Fetch
 *  @function useTrakingMaterialGradeLogFetch
 */
export const useTrakingMaterialGradeLogFetch = () => {
  const { mutate: trakingMaterialGradeLogMutate } = useMutation(
    ['trakingMaterialGradeLog'],
    ({
      category,
      click_index,
      click_result,
      click_type,
      product_type,
      search_query,
    }: ProductLogPostMaterialGradeSearchLogPayload) =>
      trackingMaterialGradeLogApi({
        category,
        click_index,
        click_result,
        click_type,
        product_type,
        search_query,
      }),
    {
      onError: (err: AxiosError<ProductLogPostMaterialGradeSearchLogError>) => {
        if (err.response?.data?.message) {
          Toast.error(err.response.data.message.text);
        }
      },
    },
  );

  return {
    trakingMaterialGradeLogMutate,
  };
};

/**
 * 조건별 판매 가능 자재 검색
 * @function useGetProductSellable
 */

export const useGetProductSellableFetch = () => {
  // response 값 캐싱 필요
  const { mutateAsync: getProductSellableFetchMutateAsync, isLoading: getProductSellableFetchIsLoading } = useMutation(
    ['getProductSellable'],
    async ({ search_conditions, agg_conditions }: SellableProductRequest) => {
      const result = await getProductSellableApi({ search_conditions, agg_conditions });
      return result.data.data;
    },
    {
      onError: (err: AxiosError<CommonAxiosErrorType>) => {
        if (err.response?.data?.message) {
          Toast.error(err.response.data.message.text);
        }
      },
    },
  );

  return {
    getProductSellableFetchMutateAsync,
    getProductSellableFetchIsLoading,
  };
};

/**
 *
 * 신규 판매 가능 자재 검색
 * @useGetSaleProductOption
 */
export const useGetSaleProductOption = (props: SaleProductOptionPayload) => {
  return useQuery(
    ['getSaleProductOption', props],
    async () => {
      const result = await getSaleProductOption(props);
      return result?.data.data;
    },
    {
      enabled: props.product_type.length > 0,
      onError: (err: AxiosError<ProductSaleGetSaleProductOptionError>) => {
        if (err.response?.data?.message) {
          Toast.error(err.response.data.message.text);
        }
      },
    },
  );
};

/**
 * 전체 ProductType 가져오기
 * @function useGetProductType
 */
export const useGetProductType = () => {
  const {
    data: getProductTypeFetchData,
    isLoading: getProductTypeFetchIsLoading,
    isError: getProductTypeFetchIsError,
  } = useQuery(
    ['getProductType'],
    async () => {
      const result = await getProductTypeApi();
      return result?.data.data;
    },
    {
      onError: (err: AxiosError<ProductGetProductCategoryError>) => {
        if (err.response?.data?.message) {
          Toast.error(err.response.data.message.text);
        }
      },
    },
  );

  const isCustomLoading = (!getProductTypeFetchData || getProductTypeFetchIsLoading) && !getProductTypeFetchIsError;

  return {
    getProductTypeFetchData,
    isCustomLoading,
  };
};

/**
 *재고 등록 가능한 ProductType 가져오기
 * @function useGetStockProductType
 */
export const useGetStockProductType = () => {
  const {
    data: getProductTypeFetchData,
    isLoading: getProductTypeFetchIsLoading,
    isError: getProductTypeFetchIsError,
  } = useQuery(
    ['useGetStockProductType'],
    async () => {
      const result = await getStockProductTypeApi();
      return result?.data.data;
    },
    {
      onError: (err: AxiosError<ProductGetProductCategoryError>) => {
        if (err.response?.data?.message) {
          Toast.error(err.response.data.message.text);
        }
      },
    },
  );

  const isCustomLoading = (!getProductTypeFetchData || getProductTypeFetchIsLoading) && !getProductTypeFetchIsError;

  return {
    getProductTypeFetchData,
    isCustomLoading,
  };
};



/**
 * ProductWeight 가져오기 (Mutation 버전)
 * @function useGetProductWeight
 */
export const useGetProductWeight = () => {
  const { mutateAsync: getProductWeightMutateAsync, isLoading: getProductWeightMutateIsLoading } = useMutation(
    ['getProductWeight'],
    async ({ category, material_grade, size1, schedule, length }: ProductGetProductWeightPayload) => {
      const result = await getProductWeightApi(
        {
          category,
          material_grade,
          size1,
          schedule,
          length,
        }
      );
      return result?.data.data;
    },
    {
      onError: (err: AxiosError<ProductGetProductCategoryError>) => {
        if (err.response?.data?.message) {
          Toast.error(err.response.data.message.text);
        }
      },
    },
  );

  return {
    getProductWeightMutateAsync,
    getProductWeightMutateIsLoading,
  };
};

/**
 * 기타 제품 카테고리의 Material Grade를 조회하는 useQuery 훅
 */
export const useGetEtcProductMaterialGrade = (payload: ProductEtcGetEtcProductMaterialGradePayload) => {
  return useQuery({
    queryKey: ['getEtcProductMaterialGrade', payload],
    queryFn: () => getEtcProductMaterialGradeApi(payload),
    enabled: !!payload.standard_code && !!payload.category_id,
    staleTime: 5 * 60 * 1000, // 5분
    cacheTime: 10 * 60 * 1000, // 10분
  });
};



export const useGetSupplierDealProduct = (code_id: number | undefined) => {
  const { data: getSupplierDealProductFetchData, isLoading: getSupplierDealProductFetchIsLoading, refetch: getSupplierDealProductFetchRefetch } = useQuery(
    ['getSupplierDealProductApi', code_id],
    async () => {
      const result = await getSupplierDealProductApi(code_id)
      return result?.data.data
    },
    {
      enabled: !!code_id,
    }
  )

  return {
    getSupplierDealProductFetchData,
    getSupplierDealProductFetchIsLoading,
    getSupplierDealProductFetchRefetch
  }
}

export const useGetSupplierDealProductOption = () => {
  const { data: getSupplierDealProductOptionFetchData, isLoading: getSupplierDealProductOptionFetchIsLoading, refetch: getSupplierDealProductOptionFetchRefetch } = useQuery(
    ['getSupplierDealProductOptionApi'],
    async () => {
      const result = await getSupplierDealProductOptionApi()
      return result?.data.data
    },
  )

  return {
    getSupplierDealProductOptionFetchData,
    getSupplierDealProductOptionFetchIsLoading,
    getSupplierDealProductOptionFetchRefetch
  }
}